import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { ShoppingBag, Globe, Store, Users } from "lucide-react";
import DataTable from "@/components/DataTable";
import { Pill, StatusFilterChip } from "@/components/StatusPill";
import { useSearchParams } from "react-router-dom";
import { columns } from "@/pages/TasksPage";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SourcePill = ({ v }) => {
  if (v === "EPOS") return <Pill colour="indigo"><span className="inline-flex items-center gap-1"><Store className="w-3 h-3" /> EPOS</span></Pill>;
  if (v === "Online") return <Pill colour="blue"><span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Online</span></Pill>;
  return <span className="text-slate-300">—</span>;
};

const sourceColumn = { key: "sales_source", label: "Source", render: (v) => <SourcePill v={v} /> };
const soldByColumn = { key: "sold_by", label: "Sold by", render: (v) => v ? <span className="text-slate-700">{v}</span> : <span className="text-slate-300">—</span> };
const shopColumns = [sourceColumn, soldByColumn, ...columns.filter((c) => !["current_department", "enquiry_source"].includes(c.key))];

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;

function TodayBySalesperson() {
  const { user } = useAuth();
  const canView = user?.is_super || (user?.permissions || []).includes("epos.reports");
  const [rows, setRows] = useState(null);
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    if (!canView) return;
    const today = new Date().toISOString().slice(0, 10);
    api.get("/epos/reports/by-salesperson", { params: { date_from: today, date_to: today } })
      .then((r) => { setRows(r.data.rows || []); setTotals(r.data.totals); })
      .catch(() => setRows([]));
  }, [canView]);

  if (!canView || !rows) return null;

  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="today-by-salesperson">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-600" /> Today by salesperson
        </h2>
        {totals ? <span className="text-sm text-slate-500">Net today <span className="font-semibold text-slate-800">{gbp(totals.net_sales)}</span></span> : null}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">No sales yet today.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <div key={r.salesperson_id || r.salesperson} className="flex items-center justify-between rounded-xl bg-slate-50 px-3.5 py-2.5" data-testid={`today-row-${r.salesperson}`}>
              <div>
                <div className="text-sm font-medium text-slate-800">{r.salesperson}</div>
                <div className="text-xs text-slate-400">{r.transaction_count} sale(s){r.refunds > 0 ? ` · ${gbp(r.refunds)} refunded` : ""}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-slate-900 tabular-nums">{gbp(r.net_sales)}</div>
                <div className="text-[11px] text-slate-400 tabular-nums">gross {gbp(r.gross_sales)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ShopPage() {
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") || "";
  const clearStatus = () => { const p = new URLSearchParams(sp); p.delete("status"); setSp(p); };
  return (
    <div data-testid="page-shop">
      <div className="px-6 lg:px-8 pt-6 lg:pt-8">
        <div className="flex items-center gap-3 mb-4">
          <PageIcon />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Shop Sales</h1>
            <p className="text-sm text-slate-500 mt-0.5">EPOS (physical shop) sales · each sale creates a job here and adjusts stock</p>
          </div>
        </div>
        <TodayBySalesperson />
      </div>
      <div className="px-6 lg:px-8 pt-4 pb-8">
      <DataTable
        dataset="tasks"
        title="Shop queue"
        hideHeader
        bare
        columns={shopColumns}
        params={{ shop_queue: true, sales_source: "EPOS", ...(status ? { job_summary_status: status } : {}) }}
        toolbarStart={status ? <StatusFilterChip value={status} onClear={clearStatus} /> : null}
        searchPlaceholder="Search title, ID, postcode, status…"
        rowLink={(r) => `/tasks/${r.legacy_id}`}
        compactToggle
      />
      </div>
    </div>
  );
}
